/**
 * OQ-294 · Kho sao lưu ngoài — nhiều kho, kết nối bằng OAuth.
 *
 * **Chủ dự án**: *"chọn 1 nhà cung cấp và bấm nút thêm, khi đó thì nhà cung cấp
 * được thêm vào danh sách backup tự động, sau đó bấm nút cấu hình của nhà cung
 * cấp đó thì sẽ hiện ra trang kết nối để cấp quyền, sau khi cấp quyền xong thì
 * nút kết nối biến mất và thay vào đó là nút kiểm tra kết nối."*
 *
 * ── Không có nút Lưu riêng ────────────────────────────────────────────────
 *
 * Mọi thao tác ở đây ghi thẳng qua API của nó (`POST /backups/remotes`, `PATCH`,
 * `DELETE`) chứ không đi qua `draft` của trang Cài đặt. Nút *Lưu thay đổi* ở
 * chân trang chỉ lo bốn khoá `backup.oauth.*`.
 *
 * Bài học OQ-270 và OQ-287: mỗi nút Lưu thừa là một chỗ để người dùng tưởng
 * mình chưa lưu, hoặc tưởng đã lưu.
 *
 * ── Nút vẽ theo `connected`, không theo `status` ───────────────────────────
 *
 * Một kho vừa cấp quyền xong nhưng chưa bấm kiểm tra vẫn `status = 'unchecked'`.
 * Đọc `status` để quyết định sẽ mời người dùng cấp quyền lần thứ hai.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, thanhApiError, API_PREFIX, type ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { ApiErrorAlert } from '../../components/ApiErrorAlert';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import { useFormat } from '../../lib/format';
import { SettingText } from './SettingsControls';
import { coChu, OTrangThai, XacNhanKhoiPhuc, type BackupCheck } from './BackupPanel';
import { OAuthAppGuide } from './OAuthAppGuide';

type NhaCungCap = 'dropbox' | 'gdrive';

interface Remote {
  id: string;
  provider: NhaCungCap;
  label: string;
  accountEmail: string | null;
  folder: string;
  keepCount: number;
  enabled: boolean;
  status: 'ok' | 'invalid' | 'unchecked';
  lastError: string | null;
  lastCheckAt: string | null;
  connected: boolean;
  createdAt: string;
}

interface TepTrenKho {
  id: string;
  name: string;
  sizeBytes: number;
  createdAt: string;
  /** OQ-304 — máy chủ đọc ra từ tên tệp; `null` cho bản tạo trước OQ-303. */
  reason?: 'auto' | 'manual' | 'safety' | null;
}

const NHA_CUNG_CAP: { id: NhaCungCap; ten: string }[] = [
  { id: 'dropbox', ten: 'Dropbox' },
  { id: 'gdrive', ten: 'Google Drive' },
];

export function RemoteBackupPanel({
  caiDat,
  onDoiCaiDat,
}: {
  caiDat: Record<string, unknown>;
  onDoiCaiDat: (key: string, value: string) => void;
}): JSX.Element {
  const t = useT();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [chon, setChon] = useState<NhaCungCap>('dropbox');
  const [khoiPhuc, setKhoiPhuc] = useState<Remote | null>(null);

  const ds = useQuery({
    queryKey: ['backup-remotes'],
    queryFn: () => api.get<Remote[]>('/settings/backups/remotes'),
  });

  const lamMoi = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['backup-remotes'] });
  };

  /*
   * Cửa sổ cấp quyền báo về bằng `postMessage` khi xong. Không có nó thì người
   * dùng phải tự bấm làm mới sau khi đóng cửa sổ — và nếu quên, danh sách vẫn
   * ghi "chưa kết nối" cho một kho vừa nối xong.
   *
   * Đăng ký MỘT LẦN (deps rỗng) và gọi `lamMoi`/`toast` qua ref ngầm của
   * closure: cả hai đều ổn định trong vòng đời panel, và đăng ký lại mỗi lần
   * render sẽ bỏ lỡ đúng thông điệp đến giữa hai lần gỡ/gắn.
   */
  useEffect(() => {
    const nghe = (e: MessageEvent): void => {
      const data = e.data as { dnshubBackupOAuth?: boolean } | null;
      if (data && typeof data.dnshubBackupOAuth === 'boolean') {
        void queryClient.invalidateQueries({ queryKey: ['backup-remotes'] });
        if (data.dnshubBackupOAuth) toast(t('settings.remote.connected'));
      }
    };
    window.addEventListener('message', nghe);
    return () => window.removeEventListener('message', nghe);
  }, [queryClient, toast, t]);

  const them = useMutation({
    mutationFn: () => api.post<Remote>('/settings/backups/remotes', { provider: chon }),
    onSuccess: async () => {
      toast(t('settings.remote.added'));
      await lamMoi();
    },
    onError: (e: unknown) => toast(thanhApiError(e).message),
  });

  const text = (key: string): string => String(caiDat[key] ?? '');

  return (
    <div className="card" data-od-id="panel-remote-backup">
      <div className="card-head">
        <div className="grow">
          <h2>{t('settings.remote.title')}</h2>
          <div className="sub">{t('settings.remote.lede')}</div>
        </div>
      </div>

      <div className="card-body stack">
        <OAuthAppGuide />

        <div className="grid g-2">
          <SettingText
            label={t('setting.backup.oauth.dropbox.client_id')}
            value={text('backup.oauth.dropbox.client_id')}
            onChange={(v) => onDoiCaiDat('backup.oauth.dropbox.client_id', v)}
            mono
          />
          <SettingText
            label={t('setting.backup.oauth.dropbox.client_secret')}
            value={text('backup.oauth.dropbox.client_secret')}
            onChange={(v) => onDoiCaiDat('backup.oauth.dropbox.client_secret', v)}
            type="password"
            mono
          />
          <SettingText
            label={t('setting.backup.oauth.gdrive.client_id')}
            value={text('backup.oauth.gdrive.client_id')}
            onChange={(v) => onDoiCaiDat('backup.oauth.gdrive.client_id', v)}
            mono
          />
          <SettingText
            label={t('setting.backup.oauth.gdrive.client_secret')}
            value={text('backup.oauth.gdrive.client_secret')}
            onChange={(v) => onDoiCaiDat('backup.oauth.gdrive.client_secret', v)}
            type="password"
            mono
          />
        </div>

        {/* ── Thêm kho ────────────────────────────────────────────────── */}
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <label className="field grow">
            <span className="label">{t('settings.remote.provider')}</span>
            <select
              value={chon}
              onChange={(e) => setChon(e.target.value as NhaCungCap)}
              data-od-id="remote-provider"
            >
              {NHA_CUNG_CAP.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.ten}
                </option>
              ))}
            </select>
          </label>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => them.mutate()}
            disabled={them.isPending}
            data-od-id="remote-add"
          >
            <Icon name="plus" size={16} />
            {t('settings.remote.add')}
          </button>
        </div>

        {/* ── Danh sách kho ───────────────────────────────────────────── */}
        <AsyncSection
          loading={ds.isLoading}
          error={ds.error}
          data={ds.data}
          onRetry={() => void ds.refetch()}
          skeleton={<SkeletonCard rows={3} />}
          empty={
            <EmptyState
              icon="cloud"
              title={t('settings.remote.emptyTitle')}
              body={t('settings.remote.emptyBody')}
            />
          }
        >
          {(list) => (
            <div className="stack" data-od-id="remote-list">
              {list.map((r) => (
                <KhoRow key={r.id} kho={r} onDoi={lamMoi} onKhoiPhuc={() => setKhoiPhuc(r)} />
              ))}
            </div>
          )}
        </AsyncSection>
      </div>

      {khoiPhuc ? <KhoiPhucModal kho={khoiPhuc} onClose={() => setKhoiPhuc(null)} /> : null}
    </div>
  );
}

function KhoRow({
  kho,
  onDoi,
  onKhoiPhuc,
}: {
  kho: Remote;
  onDoi: () => Promise<void>;
  onKhoiPhuc: () => void;
}): JSX.Element {
  const t = useT();
  const { toast } = useToast();
  const { formatRelative } = useFormat();
  const [xacNhanXoa, setXacNhanXoa] = useState(false);

  const capQuyen = useMutation({
    mutationFn: () => api.get<{ url: string }>(`/settings/backups/remotes/${kho.id}/authorize`),
    onSuccess: ({ url }) => {
      /*
       * Cửa sổ con, không phải tab: người dùng thấy rõ đây là một việc phụ và
       * quay lại đúng trang cũ khi xong. Trình duyệt chặn `window.open` nếu
       * không phải phản ứng trực tiếp của một cú bấm — nên phải mở ở đây, trong
       * `onSuccess` của chính lần bấm ấy.
       */
      window.open(url, 'dnshub-oauth', 'width=520,height=680');
    },
    onError: (e: unknown) => toast(thanhApiError(e).message),
  });

  const kiemTra = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; message?: string; files: number }>(
        `/settings/backups/remotes/${kho.id}/test`,
      ),
    onSuccess: async (kq) => {
      toast(kq.ok ? t('settings.remote.testOk', { n: kq.files }) : (kq.message ?? ''));
      await onDoi();
    },
    onError: (e: unknown) => toast(thanhApiError(e).message),
  });

  const xoa = useMutation({
    mutationFn: () => api.del(`/settings/backups/remotes/${kho.id}`),
    onSuccess: async () => {
      setXacNhanXoa(false);
      toast(t('settings.remote.removed'));
      await onDoi();
    },
  });

  const doiKho = useMutation({
    mutationFn: (patch: Partial<Remote>) => api.patch(`/settings/backups/remotes/${kho.id}`, patch),
    onSuccess: () => void onDoi(),
  });

  const tone = kho.status === 'ok' ? 'ok' : kho.status === 'invalid' ? 'bad' : '';

  return (
    <div className="remote-row" data-od-id={`remote-row-${kho.id}`}>
      <Icon name="cloud" size={18} />

      <div className="remote-row-info">
        <b>{kho.label}</b>
        <div className="sub small muted">
          {kho.accountEmail ?? t('settings.remote.notConnected')}
          {kho.lastCheckAt ? ` · ${formatRelative(kho.lastCheckAt)}` : ''}
        </div>
        {kho.status === 'invalid' && kho.lastError ? (
          <div className="sub small" style={{ color: 'var(--bad)' }}>
            {kho.lastError}
          </div>
        ) : null}
      </div>

      <span className={`chip ${tone}`}>{t(`settings.remote.status.${kho.status}`)}</span>

      {/*
        Đúng như chủ dự án mô tả: chưa cấp quyền thì hiện Kết nối; cấp xong thì
        nút ấy BIẾN MẤT, thay bằng Kiểm tra kết nối và Xoá kết nối.
      */}
      <div className="remote-row-actions">
        {kho.connected ? (
          <>
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => kiemTra.mutate()}
              disabled={kiemTra.isPending}
              data-od-id={`remote-test-${kho.id}`}
            >
              {kiemTra.isPending ? t('common.processing') : t('settings.remote.test')}
            </button>
            <button
              className="btn btn-sm"
              type="button"
              onClick={onKhoiPhuc}
              data-od-id={`remote-restore-${kho.id}`}
            >
              {t('settings.remote.restoreFrom')}
            </button>
          </>
        ) : (
          <button
            className="btn btn-sm btn-primary"
            type="button"
            onClick={() => capQuyen.mutate()}
            disabled={capQuyen.isPending}
            data-od-id={`remote-connect-${kho.id}`}
          >
            <Icon name="external" size={14} />
            {t('settings.remote.connect')}
          </button>
        )}

        {/*
          Công tắc bật/tắt, KHÔNG phải một nút biểu tượng.

          Chủ dự án hỏi thẳng: *"Nút x có nghĩa gì?"* — và đó là câu trả lời đủ.
          Trước đây chỗ này là `icon-btn` với dấu ✓ hoặc ✕: hai biểu tượng
          không nói được chúng đang **báo trạng thái** hay **mời bấm để đổi**,
          và `aria-label` thì mắt không đọc thấy.

          Vòng hai (OQ-298): nhãn cố định *"Đang bật"* vẫn chưa đủ. Nó đứng yên
          khi công tắc đổi, nên người đọc không biết mình đang xem **trạng thái
          hiện tại** hay **việc sẽ xảy ra nếu bấm**. Chủ dự án chốt: nhãn phải
          đổi theo trạng thái, và rê chuột thì nói rõ trạng thái ấy nghĩa là gì —
          vì "bật" một kho sao lưu có thể hiểu thành "kho còn kết nối", trong khi
          nó thật ra quyết định kho có NHẬN bản sao lưu mới hay không.
        */}
        <label
          className="remote-row-switch"
          title={t(kho.enabled ? 'settings.remote.enabledHint' : 'settings.remote.disabledHint')}
        >
          <input
            type="checkbox"
            checked={kho.enabled}
            onChange={() => doiKho.mutate({ enabled: !kho.enabled })}
            data-od-id={`remote-toggle-${kho.id}`}
          />
          <span>{t(kho.enabled ? 'settings.remote.enabled' : 'settings.remote.disabled')}</span>
        </label>

        <button
          className="icon-btn"
          type="button"
          aria-label={t('settings.remote.remove')}
          title={t('settings.remote.remove')}
          onClick={() => setXacNhanXoa(true)}
          data-od-id={`remote-remove-${kho.id}`}
        >
          <Icon name="trash" size={15} />
        </button>
      </div>

      {xacNhanXoa ? (
        <Modal open title={t('settings.remote.removeTitle')} onClose={() => setXacNhanXoa(false)}>
          <p className="muted">{t('settings.remote.removeBody', { label: kho.label })}</p>
          {/*
            Nói rõ bản sao lưu KHÔNG bị xoá theo. Không có câu này, người dùng
            phải đoán — và đoán sai theo hướng nào cũng tệ: hoặc họ không dám gỡ
            một kết nối hỏng, hoặc họ gỡ và lo mất hết.
          */}
          <div className="form-alert" role="note">
            <Icon name="info" size={16} />
            <span>{t('settings.remote.removeKeepsFiles')}</span>
          </div>
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn" type="button" onClick={() => setXacNhanXoa(false)}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => xoa.mutate()}
              disabled={xoa.isPending}
              data-od-id="remote-remove-confirm"
            >
              {xoa.isPending ? t('common.processing') : t('common.delete')}
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function KhoiPhucModal({ kho, onClose }: { kho: Remote; onClose: () => void }): JSX.Element {
  const t = useT();
  const { toast } = useToast();
  const { formatDateTime } = useFormat();
  const [loi, setLoi] = useState<ApiError | null>(null);
  const [kiem, setKiem] = useState<Record<string, BackupCheck>>({});
  const [dangKiem, setDangKiem] = useState<string | null>(null);
  const [xoaXacNhan, setXoaXacNhan] = useState<TepTrenKho | null>(null);
  const [xacNhanPhucHoi, setXacNhanPhucHoi] = useState<TepTrenKho | null>(null);

  const ds = useQuery({
    queryKey: ['backup-remote-files', kho.id],
    queryFn: () =>
      api.get<{ provider: string; files: TepTrenKho[]; error?: string }>(
        `/settings/backups/remotes/${kho.id}/files`,
      ),
  });

  const phucHoi = useMutation({
    mutationFn: (fid: string) =>
      api.post(`/settings/backups/remotes/${kho.id}/restore/${encodeURIComponent(fid)}`),
    onSuccess: () => {
      toast(t('settings.remote.restored'));
      setXacNhanPhucHoi(null);
      onClose();
    },
    onError: (e: unknown) => setLoi(thanhApiError(e)),
  });

  /*
   * OQ-303 — **Chủ dự án**: *"Khôi phục từ dropbox vẫn chưa có nút kiểm tra.
   * Thêm đầy đủ các nút kiểm tra, tải về và xoá cho tôi"*.
   *
   * Kiểm phải TẢI TỆP VỀ máy chủ mới mở được — không có cách nào đọc một tệp
   * còn nằm ở Dropbox. Nên nó chậm hơn hẳn lượt kiểm bản tại chỗ, và trạng thái
   * "đang kiểm" ở đây không phải trang trí.
   */
  const kiemTra = useMutation({
    mutationFn: async (f: TepTrenKho) => {
      setDangKiem(f.id);
      return api.get<BackupCheck>(
        `/settings/backups/remotes/${kho.id}/check/${encodeURIComponent(f.id)}`,
      );
    },
    onSuccess: (res, f) => setKiem((truoc) => ({ ...truoc, [f.id]: res })),
    onError: (e: unknown) => setLoi(thanhApiError(e)),
    onSettled: () => setDangKiem(null),
  });

  const xoa = useMutation({
    mutationFn: (f: TepTrenKho) =>
      api.del(`/settings/backups/remotes/${kho.id}/file/${encodeURIComponent(f.id)}`),
    onSuccess: () => {
      toast(t('settings.remote.fileDeleted'));
      setXoaXacNhan(null);
      void ds.refetch();
    },
    onError: (e: unknown) => {
      setXoaXacNhan(null);
      setLoi(thanhApiError(e));
    },
  });

  return (
    <Modal
      open
      wide
      title={t('settings.remote.restoreTitle', { label: kho.label })}
      onClose={onClose}
    >
      <ApiErrorAlert error={loi} />

      <AsyncSection
        loading={ds.isLoading}
        error={ds.error}
        data={ds.data?.files}
        onRetry={() => void ds.refetch()}
        skeleton={<SkeletonCard rows={4} />}
        empty={
          <EmptyState
            icon="cloud"
            title={t('settings.remote.noFilesTitle')}
            body={ds.data?.error ?? t('settings.remote.noFilesBody')}
          />
        }
      >
        {(files) => (
          <div className="table-wrap">
            <table className="tbl" data-od-id="remote-files-table">
              <thead>
                <tr>
                  <th scope="col">{t('settings.backup.col.name')}</th>
                  <th scope="col">{t('settings.backup.col.size')}</th>
                  <th scope="col">{t('settings.backup.col.created')}</th>
                  <th scope="col">{t('settings.backup.col.reason')}</th>
                  <th scope="col">{t('settings.backup.col.check')}</th>
                  <th scope="col" className="right" />
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.id}>
                    <td className="mono small">{f.name}</td>
                    <td className="num small">{coChu(f.sizeBytes)}</td>
                    <td className="small muted">{formatDateTime(f.createdAt)}</td>
                    {/*
                      OQ-304 — cùng cột, cùng nhãn với bảng bản tại chỗ. Chủ dự
                      án phải chọn giữa hai danh sách này lúc chữa cháy; hai cách
                      trình bày khác nhau cho cùng một loại dữ liệu là thứ bắt
                      người đọc học lại từ đầu ở đúng lúc không nên phải học.
                    */}
                    <td className="small" data-od-id={`remote-reason-${f.id}`}>
                      {f.reason ? (
                        <span className="chip muted">
                          {t(`settings.backup.reason.${f.reason}`)}
                        </span>
                      ) : (
                        <span className="muted">{t('settings.backup.reason.unknown')}</span>
                      )}
                    </td>
                    <td>
                      <OTrangThai check={kiem[f.id]} dangKiem={dangKiem === f.id} />
                    </td>
                    <td className="right">
                      <div className="row-actions">
                        <button
                          className="btn btn-sm"
                          type="button"
                          onClick={() => kiemTra.mutate(f)}
                          disabled={dangKiem !== null || phucHoi.isPending}
                          data-od-id={`remote-check-file-${f.id}`}
                        >
                          <Icon name="refresh" size={14} />
                          {t('settings.backup.check')}
                        </button>
                        <a
                          className="btn btn-sm"
                          href={`${API_PREFIX}/settings/backups/remotes/${kho.id}/download/${encodeURIComponent(f.id)}`}
                          data-od-id={`remote-download-file-${f.id}`}
                        >
                          {t('settings.backup.download')}
                        </a>
                        {/*
                          Khôi phục phải qua hộp gõ lại tên — OQ-304.

                          Chủ dự án: *"khi khôi phục từ local thì có cảnh báo
                          nhưng khôi phục từ dropbox thì không"*. Cùng một thao
                          tác ghi đè toàn bộ CSDL mà một đường có rào, một đường
                          không: cái rào ở đường kia dạy người dùng rằng khôi
                          phục là việc phải cân nhắc, rồi đường này phản bội
                          đúng bài học ấy.
                        */}
                        <button
                          className="btn btn-sm"
                          type="button"
                          onClick={() => setXacNhanPhucHoi(f)}
                          disabled={phucHoi.isPending || dangKiem !== null}
                          data-od-id={`remote-restore-file-${f.id}`}
                        >
                          {t('settings.backup.restore')}
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          type="button"
                          onClick={() => setXoaXacNhan(f)}
                          disabled={phucHoi.isPending || dangKiem !== null}
                          data-od-id={`remote-delete-file-${f.id}`}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {/* Khôi phục là thao tác ghi đè — nói trước rằng hệ thống tự tạo bản an
          toàn, để người bấm biết mình có đường lùi. */}
      <div className="form-alert warn" role="note" style={{ marginTop: 12 }}>
        <Icon name="warn" size={16} />
        <span>{t('settings.remote.restoreWarn')}</span>
      </div>

      {/*
        Cùng hộp xác nhận với bảng bản tại chỗ — cùng component, không phải bản
        chép. Hai bản chép sẽ lệch nhau theo thời gian, và cách lệch đầu tiên
        chính là cái chủ dự án vừa chỉ ra: một bên có rào, bên kia bị quên.
      */}
      {xacNhanPhucHoi ? (
        <XacNhanKhoiPhuc
          ten={xacNhanPhucHoi.name}
          check={kiem[xacNhanPhucHoi.id]}
          dangChay={phucHoi.isPending}
          onHuy={() => setXacNhanPhucHoi(null)}
          onXacNhan={() => phucHoi.mutate(xacNhanPhucHoi.id)}
          odId="remote-restore-confirm"
        />
      ) : null}

      {/*
        Xoá một bản sao lưu trên kho ngoài là thao tác KHÔNG lùi được: khác bản
        tại chỗ, ở đây không có thùng rác nào và không có lượt sao lưu nào chép
        lại nó. Nên phải hỏi, và phải hỏi kèm đúng tên tệp — một hộp thoại
        "Bạn có chắc?" trơn thì người ta bấm Đồng ý theo phản xạ.
      */}
      {xoaXacNhan ? (
        <Modal
          open
          title={t('settings.remote.deleteFileTitle')}
          onClose={() => setXoaXacNhan(null)}
        >
          <p className="muted">
            {t('settings.remote.deleteFileBody', { name: xoaXacNhan.name, label: kho.label })}
          </p>
          {/* Cùng khuôn với hộp xác nhận gỡ kho ở trên — hai hộp cùng vai thì
              phải xếp nút giống nhau, nếu không người ta phải đọc lại vị trí nút
              mỗi lần. */}
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn" type="button" onClick={() => setXoaXacNhan(null)}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => xoa.mutate(xoaXacNhan)}
              disabled={xoa.isPending}
              data-od-id="remote-delete-file-confirm"
            >
              {t('common.delete')}
            </button>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
